if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      // The viewer is a horizontal scroll-snap track (swipe, trackpad, mouse
      // drag, arrow keys). The active slide drives the thumbnails and the
      // "next media" preview under the viewer, which shows the following
      // slide and wraps to the first. Slides are always read from the DOM
      // because product-info.js reorders them on variant change.
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          nextMedia: this.querySelector('[data-next-media]'),
        };
        this.track = this.elements.viewer?.querySelector('.product__media-list');
        this.mql = window.matchMedia('(min-width: 750px)');
        this.railMql = window.matchMedia('(min-width: 990px)');
        if (!this.track) return;

        this.activeIndex = Math.max(
          this.getSlides().findIndex((slide) => slide.classList.contains('is-active')),
          0
        );

        this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
        if ('onscrollend' in window) this.track.addEventListener('scrollend', this.onScrollEnd.bind(this));
        this.track.addEventListener('keydown', this.onKeydown.bind(this));
        this.initDrag();

        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => {
          thumbnail
            .querySelector('button')
            ?.addEventListener('click', () => this.setActiveMedia(thumbnail.dataset.target, false));
        });

        this.elements.nextMedia?.addEventListener('click', () => {
          const target = this.elements.nextMedia.dataset.target;
          if (target) this.setActiveMedia(target, false);
        });

        this.resizeObserver = new ResizeObserver(() => this.alignToActive());
        this.resizeObserver.observe(this.track);

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.alignToActive();
        this.setActive(this.activeIndex, { announce: false, pause: false });
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      getSlides() {
        return Array.from(this.track.querySelectorAll(':scope > [data-media-id]'));
      }

      indexFromScroll() {
        const width = this.track.clientWidth;
        if (!width) return this.activeIndex;
        const count = this.getSlides().length;
        return Math.min(Math.max(Math.round(this.track.scrollLeft / width), 0), count - 1);
      }

      onScroll() {
        if (this.isDragging) return;
        // Programmatic scrolls (thumbnail / preview clicks) already set the
        // target; skip the slides they pass on the way to avoid flicker.
        if (this.pendingIndex == null) {
          const index = this.indexFromScroll();
          if (index !== this.activeIndex) this.setActive(index);
        }
        if (!('onscrollend' in window)) {
          clearTimeout(this.scrollEndTimer);
          this.scrollEndTimer = setTimeout(() => this.onScrollEnd(), 120);
        }
      }

      onScrollEnd() {
        if (this.isDragging) return;
        this.pendingIndex = null;
        const index = this.indexFromScroll();
        if (index !== this.activeIndex) this.setActive(index);
      }

      onKeydown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const count = this.getSlides().length;
        const step = event.key === 'ArrowRight' ? 1 : -1;
        this.goTo(Math.min(Math.max(this.activeIndex + step, 0), count - 1));
      }

      // Mouse drag for desktop; touch and trackpads use native scrolling.
      initDrag() {
        let startX = 0;
        let startScroll = 0;
        let pointerId = null;

        this.track.addEventListener('pointerdown', (event) => {
          this.dragMoved = false;
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          if (this.getSlides().length < 2 || event.target.closest('.deferred-media, model-viewer, video, iframe')) return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startScroll = this.track.scrollLeft;
        });

        this.track.addEventListener('pointermove', (event) => {
          if (event.pointerId !== pointerId) return;
          const delta = event.clientX - startX;
          if (!this.isDragging) {
            if (Math.abs(delta) < 5) return;
            this.isDragging = true;
            this.dragMoved = true;
            this.track.setPointerCapture(pointerId);
            this.track.classList.add('is-dragging');
          }
          this.track.scrollLeft = startScroll - delta;
        });

        const endDrag = (event) => {
          if (event.pointerId !== pointerId) return;
          pointerId = null;
          if (!this.isDragging) return;
          this.isDragging = false;
          this.track.classList.remove('is-dragging');
          const width = this.track.clientWidth;
          const delta = this.track.scrollLeft - startScroll;
          let index = Math.round(startScroll / width);
          if (Math.abs(delta) > width * 0.15) index += delta > 0 ? 1 : -1;
          this.goTo(Math.min(Math.max(index, 0), this.getSlides().length - 1));
        };
        this.track.addEventListener('pointerup', endDrag);
        this.track.addEventListener('pointercancel', endDrag);

        // A drag must not open the lightbox on release.
        this.track.addEventListener(
          'click',
          (event) => {
            if (!this.dragMoved) return;
            this.dragMoved = false;
            event.preventDefault();
            event.stopPropagation();
          },
          true
        );
        this.track.addEventListener('dragstart', (event) => event.preventDefault());
      }

      goTo(index, { smooth = true } = {}) {
        const slide = this.getSlides()[index];
        if (!slide) return;
        this.pendingIndex = smooth ? index : null;
        this.track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
        if (index !== this.activeIndex) this.setActive(index);
        // Nothing to scroll (already there): release the lock straight away.
        if (Math.abs(this.track.scrollLeft - slide.offsetLeft) < 1) this.pendingIndex = null;
      }

      alignToActive() {
        const slide = this.getSlides()[this.activeIndex];
        if (!slide || this.isDragging) return;
        if (Math.abs(this.track.scrollLeft - slide.offsetLeft) >= 1) {
          this.track.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
        }
      }

      setActive(index, { announce = true, pause = true } = {}) {
        const slides = this.getSlides();
        const activeSlide = slides[index];
        if (!activeSlide) return;

        const changed = index !== this.activeIndex;
        this.activeIndex = index;
        slides.forEach((slide) => slide.classList.toggle('is-active', slide === activeSlide));
        if (changed && pause) window.pauseAllMedia?.();

        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${activeSlide.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
        this.updateNextMedia(slides, index);
        if (announce && changed && thumbnail) this.announceLiveRegion(activeSlide, thumbnail.dataset.mediaPosition);
      }

      updateNextMedia(slides, index) {
        const preview = this.elements.nextMedia;
        if (!preview) return;

        const next = slides.length > 1 ? slides[(index + 1) % slides.length] : null;
        const source = next?.querySelector('img');
        preview.hidden = !source;
        if (!source) return;

        const image = preview.querySelector('.product__next-media-image');
        preview.dataset.target = next.dataset.mediaId;
        preview.classList.toggle('product__next-media--video', !next.querySelector('.product__modal-opener--image'));
        if (image.dataset.mediaId === next.dataset.mediaId) return;

        image.dataset.mediaId = next.dataset.mediaId;
        image.classList.add('is-loading');
        image.onload = () => image.classList.remove('is-loading');
        if (source.srcset) image.srcset = source.srcset;
        else image.removeAttribute('srcset');
        image.src = source.getAttribute('src');
        image.alt = source.alt || '';
        if (image.complete) image.classList.remove('is-loading');
      }

      setActiveMedia(mediaId, prepend) {
        let slides = this.getSlides();
        let activeMedia = slides.find((slide) => slide.dataset.mediaId === mediaId) || slides[0];
        if (!activeMedia) return;

        if (prepend) {
          if (this.track.firstElementChild !== activeMedia) this.track.prepend(activeMedia);

          const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail && activeThumbnail.parentElement.firstElementChild !== activeThumbnail) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          if (this.elements.thumbnails?.slider) this.elements.thumbnails.resetPages();
          slides = this.getSlides();
        }

        const index = slides.indexOf(activeMedia);
        this.preventStickyHeader();
        this.goTo(index, { smooth: !prepend });
        if (prepend) this.setActive(index, { announce: false });

        window.setTimeout(() => {
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          window.scrollTo({ top: activeMediaRect.top + window.scrollY, behavior: 'smooth' });
        });

        if (!prepend) this.playActiveMedia(activeMedia);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        // Scroll the rail itself (never scrollIntoView, which moves the page):
        // vertical rail on desktop, horizontal row below it.
        const list = thumbnail.parentElement;
        const listRect = list.getBoundingClientRect();
        const rect = thumbnail.getBoundingClientRect();
        if (this.railMql.matches) {
          const top = rect.top - listRect.top + list.scrollTop;
          if (top < list.scrollTop || top + rect.height > list.scrollTop + list.clientHeight + 1) {
            list.scrollTo({ top, behavior: 'smooth' });
          }
        } else {
          const left = rect.left - listRect.left + list.scrollLeft;
          if (left < list.scrollLeft || left + rect.width > list.scrollLeft + list.clientWidth + 1) {
            list.scrollTo({ left, behavior: 'smooth' });
          }
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
